import { css, etiColors, mediaQueries } from '@eti/styles';
import { ContentWrapper, LoadingDots, variants } from '@eti/components';
import { breakpoints, Button, Inline, Stack, Text, textVariants } from '@etg/wings';
import { useMediaQuery } from 'usehooks-ts';
import { useTranslation } from '@eti/providers';
import { price as priceUtils } from '@eti/utils';
import type {
  BoundNew,
  BrandedFare,
  PaymentMethodPrice,
  TravelerNew,
  TravelerPrice,
} from '@eti/schema-types';
import { PriceInformation } from '../price-information/PriceInformation';
import type { BoundType } from '../../../order-details/models';
import { useToggle } from '../../../../common/custom-hooks/toggle';

import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';

const sectionStyles = css`
  align-items: center;
  background-color: ${etiColors.white};
  bottom: 0;
  box-shadow: 0 -2px 8px 0 #0000000d;
  display: flex;
  margin-top: auto;
  min-height: 84px;
  position: sticky;
`;

const contentWrapperStyles = css`
  display: flex;
  justify-content: space-between;
`;

const inlineStyles = css`
  justify-content: space-between;
  width: 100%;
`;

const customButtonStyles = css`
  min-width: 134px;

  @media ${mediaQueries.small.only} {
    min-width: 114px;
  }
`;

const textStyles = css`
  color: #737373;
`;

const textHoverStyles = css`
  cursor: pointer;
`;

const DISPLAY_PRICE_STRING_LIMIT = 15;
interface StickyFooterProps {
  bounds: BoundType[] | BoundNew[];
  dataTestid?: string;
  discountAmount: number;
  finalProductIdsToPurchase?: number[];
  isSelectTripLoading: boolean;
  onClick: () => void;
  totalPrice: string;
  paymentMethodPrices: PaymentMethodPrice[];
  selectedBrandedFare?: BrandedFare;
  travelerPrices: TravelerPrice[];
  travelerPricesWithoutDiscounts: TravelerPrice[];
  tripTravelers: TravelerNew[];
}

const StickyFooter = ({
  bounds,
  dataTestid = 'sticky-footer',
  discountAmount,
  finalProductIdsToPurchase = [],
  isSelectTripLoading,
  onClick,
  totalPrice,
  paymentMethodPrices,
  selectedBrandedFare,
  travelerPrices,
  travelerPricesWithoutDiscounts,
  tripTravelers,
}: StickyFooterProps) => {
  const { t } = useTranslation();
  const { isOpen, handleOpen, handleClose } = useToggle();
  const isMobileDevice = useMediaQuery(`(max-width: ${breakpoints._560})`);
  const perAdultText = t('Result.PricePerAdult.Text');

  const priceInformation = (
    <PriceInformation
      bounds={bounds as BoundNew[]}
      discountAmount={discountAmount}
      finalProductIdsToPurchase={finalProductIdsToPurchase}
      handleClose={handleClose}
      handleOpen={handleOpen}
      isOpen={isOpen}
      paymentMethodPrices={paymentMethodPrices}
      selectedFare={selectedBrandedFare}
      travelerPrices={travelerPrices}
      travelerPricesWithoutDiscounts={travelerPricesWithoutDiscounts}
      tripTravelers={tripTravelers}
    />
  );

  const isDisplayPriceTooLong = (totalPrice + perAdultText).length > DISPLAY_PRICE_STRING_LIMIT;

  return (
    <section className={sectionStyles} data-testid={dataTestid}>
      <ContentWrapper className={contentWrapperStyles}>
        <Inline alignY="center" className={inlineStyles}>
          {isMobileDevice && (
            <Stack spacing={4}>
              <Inline alignY="center" spacing={4}>
                <Text data-testid="display-price-text-mobile">
                  {t('YourSelectedTrip.Page.BrandedFares.TotalPrice')}
                </Text>
                <p className={screenReadersOnlyStyles}>
                  {`${priceUtils.replaceWithCurrencyName(totalPrice)} ${t('Price.Per.Adult')}`}
                </p>
                {priceInformation}
              </Inline>
              <Inline alignY="center" spacing={8}>
                <Text
                  aria-hidden
                  className={textHoverStyles}
                  data-testid="display-price-mobile"
                  onClick={handleOpen}
                  variant={textVariants._18_EMPHASIZED}
                >
                  {totalPrice}
                </Text>
                {!isDisplayPriceTooLong && (
                  <Text aria-hidden as="p" className={textStyles} data-testid="adult-text-mobile">
                    {perAdultText}
                  </Text>
                )}
              </Inline>
              {isDisplayPriceTooLong && (
                <Text aria-hidden as="p" className={textStyles} data-testid="adult-text-mobile">
                  {perAdultText}
                </Text>
              )}
            </Stack>
          )}
          {!isMobileDevice && (
            <Text data-testid="display-price-text-desktop" variant={textVariants._16_BOLD}>
              {t('YourSelectedTrip.Page.BrandedFares.TotalPrice')}
            </Text>
          )}
          <Inline alignY="center">
            {!isMobileDevice && (
              <Stack align="end" spacing={0}>
                <Inline alignY="center" onClick={handleOpen} spacing={4}>
                  <Text className={screenReadersOnlyStyles}>
                    {`${priceUtils.replaceWithCurrencyName(totalPrice)} ${t('Price.Per.Adult')}`}
                  </Text>
                  {priceInformation}
                  <Text
                    aria-hidden
                    className={textHoverStyles}
                    data-testid="display-price-desktop"
                    variant={textVariants._24_EMPHASIZED}
                  >
                    {totalPrice}
                  </Text>
                </Inline>
                <Text aria-hidden as="p" className={textStyles}>
                  {perAdultText}
                </Text>
              </Stack>
            )}

            <Button
              className={customButtonStyles}
              data-testid={`${dataTestid}-proceedButton`}
              onClick={onClick}
              variant={variants.PRIMARY}
            >
              {isSelectTripLoading ? (
                <LoadingDots />
              ) : (
                t('YourSelectedTrip.Page.ProceedButton.Label')
              )}
            </Button>
          </Inline>
        </Inline>
      </ContentWrapper>
    </section>
  );
};

export default StickyFooter;
